// MapPontoResidencia.tsx
import { useEffect, useState } from "react";
import { MapContainer, TileLayer, useMap, Circle } from "react-leaflet";
import { ClockInMarker } from "../mapUtils/ClockInMarker";
import { ClockOutMarker } from "../mapUtils/ClockOutMarker";
import { BranchesMarker } from "../mapUtils/BranchesMarker";
import axios from "axios";
import { headquarter } from "../../utils/headquarter-localization";
import "leaflet/dist/leaflet.css";
import { PinHouseMarker } from "../mapUtils/PinHouseMarker";

interface Branch {
  filial: string;
  nome: string;
  endereco: string;
  bairro: string;
  cidade: string;
  estado: string;
  cep: string;
  latitude: number;
  longitude: number;
}

interface ClockInOut {
  hora: string;
  latitude: number;
  longitude: number;
}

interface PontoData {
  matricula: string;
  nome: string;
  cidade: string;
  endereco: string;
  residencia?: {
    latitude: number;
    longitude: number;
  };
  batidas?: {
    entrada1?: ClockInOut;
    saida1?: ClockInOut;
    entrada2?: ClockInOut;
    saida2?: ClockInOut;
  };
}

interface MapPontoResidenciaProps {
  pontoData?: PontoData;
}

// Componente para atualizar o centro do mapa
function ChangeMapView({ center }: { center: [number, number] }) {
  const map = useMap();
  useEffect(() => {
    map.setView(center);
  }, [center, map]);
  return null;
}

export function MapPontoResidencia({ pontoData }: MapPontoResidenciaProps) {
  const [branches, setBranches] = useState<Branch[]>([]);
  const [mapCenter, setMapCenter] = useState<[number, number]>([
    headquarter.latitude,
    headquarter.longitude,
  ]);

  // Definição para estilo do círculo de residência
  const residenceCircleStyle = {
    fillColor: "green",
    fillOpacity: 0.1,
    color: "green",
    weight: 2,
  };

  // Definição para estilo do círculo das filiais
  const branchCircleStyle = {
    fillColor: "blue",
    fillOpacity: 0.1,
    color: "blue",
    weight: 2,
  };

  // Raio de 1km em metros
  const radius = 1000;

  // Buscar as filiais
  useEffect(() => {
    async function fetchBranches() {
      try {
        const { data } = await axios.get<Branch[]>(
          "https://radar.tlpservicos.com.br/api/company/locations"
        );
        setBranches(data);
      } catch (error) {
        console.error("Erro ao carregar filiais:", error);
      }
    }

    fetchBranches();
  }, []);

  // Atualizar o centro do mapa
  useEffect(() => {
    if (pontoData?.residencia?.latitude && pontoData?.residencia?.longitude) {
      setMapCenter([pontoData.residencia.latitude, pontoData.residencia.longitude]);
    } else {
      setMapCenter([headquarter.latitude, headquarter.longitude]);
    }
  }, [pontoData?.residencia]);

  const renderBatidasMarkers = () => {
    if (!pontoData?.batidas) return null;

    const markers = [];
    const { batidas } = pontoData;

    // Entrada 1
    if (
      batidas.entrada1 &&
      typeof batidas.entrada1.latitude === "number" &&
      typeof batidas.entrada1.longitude === "number"
    ) {
      markers.push(
        <ClockInMarker
          key="entrada1"
          position={[batidas.entrada1.latitude, batidas.entrada1.longitude]}
          popupContent={
            <>
              Primeira Entrada
              <br />
              Horário: {batidas.entrada1.hora}
            </>
          }
        />
      );
    }

    // Saída 1
    if (
      batidas.saida1 &&
      typeof batidas.saida1.latitude === "number" &&
      typeof batidas.saida1.longitude === "number"
    ) {
      markers.push(
        <ClockOutMarker
          key="saida1"
          position={[batidas.saida1.latitude, batidas.saida1.longitude]}
          popupContent={
            <>
              Primeira Saída
              <br />
              Horário: {batidas.saida1.hora}
            </>
          }
        />
      );
    }

    // Entrada 2
    if (
      batidas.entrada2 &&
      typeof batidas.entrada2.latitude === "number" &&
      typeof batidas.entrada2.longitude === "number"
    ) {
      markers.push(
        <ClockInMarker
          key="entrada2"
          position={[batidas.entrada2.latitude, batidas.entrada2.longitude]}
          popupContent={
            <>
              Segunda Entrada
              <br />
              Horário: {batidas.entrada2.hora}
            </>
          }
        />
      );
    }

    // Saída 2
    if (
      batidas.saida2 &&
      typeof batidas.saida2.latitude === "number" &&
      typeof batidas.saida2.longitude === "number"
    ) {
      markers.push(
        <ClockOutMarker
          key="saida2"
          position={[batidas.saida2.latitude, batidas.saida2.longitude]}
          popupContent={
            <>
              Segunda Saída
              <br />
              Horário: {batidas.saida2.hora}
            </>
          }
        />
      );
    }

    return markers;
  };

  return (
    <div className="w-full h-full flex flex-col dark:bg-gray-350 bg-gray-50 rounded-lg overflow-hidden relative">
      <div className="dark:bg-gray-400 bg-gray-100 h-6 drag-handle cursor-move flex items-center px-2 rounded-t-md">
        Mapa de Registro de Ponto
      </div>
      <div className="flex-grow relative">
        <MapContainer
          center={mapCenter}
          zoom={13}
          scrollWheelZoom={true}
          style={{
            height: "calc(100% - 20px)",
            width: "calc(100% - 20px)",
            margin: "10px",
          }}
        >
          <ChangeMapView center={mapCenter} />
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />

          {/* Sempre mostrar as filiais com círculos de 1km */}
          {branches.map((branch, index) => (
            <div key={index}>
              <BranchesMarker
                position={[branch.latitude, branch.longitude]}
                popupContent={
                  <>
                    Filial: {branch.filial}
                    <br />
                    Endereço: {`${branch.endereco} ${branch.estado}`}
                    <br />
                    Latitude: {branch.latitude}
                    <br />
                    Longitude: {branch.longitude}
                  </>
                }
              />
              {/* Círculo de 1km ao redor da filial */}
              <Circle
                center={[branch.latitude, branch.longitude]}
                radius={radius}
                pathOptions={branchCircleStyle}
              />
            </div>
          ))}

          {/* Residência e círculo de 1km */}
          {pontoData &&
            pontoData.residencia &&
            typeof pontoData.residencia.latitude === "number" &&
            typeof pontoData.residencia.longitude === "number" && (
              <>
                <PinHouseMarker
                  position={[
                    pontoData.residencia.latitude,
                    pontoData.residencia.longitude,
                  ]}
                  popupContent={
                    <>
                      Residência de {pontoData.nome}
                      <br />
                      Endereço: {pontoData.endereco}
                      <br />
                      Cidade: {pontoData.cidade}
                    </>
                  }
                />
                {/* Círculo de 1km ao redor da residência */}
                <Circle
                  center={[
                    pontoData.residencia.latitude,
                    pontoData.residencia.longitude,
                  ]}
                  radius={radius}
                  pathOptions={residenceCircleStyle}
                />
              </>
            )}

          {/* Marcadores de entrada e saída */}
          {renderBatidasMarkers()}
        </MapContainer>
      </div>
    </div>
  );
}
